import { useState } from "react";
import { BookOpen, Copy, LayoutTemplate, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { RecipeDialog } from "@/components/RecipeDialog";
import { EmptyState, PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { ProductImage } from "@/components/ProductImage";
import { api, brl, errMsg, pct } from "@/lib/api";

export const TemplatesDialog = ({ open, onOpenChange, onApplied }) => {
  const { data } = useApi(open ? "/templates" : null);
  const [busy, setBusy] = useState("");
  const apply = async (t) => {
    setBusy(t.id);
    try { const r = await api.post(`/templates/${t.id}/apply`); toast.success(`"${t.name}" adicionado com seus insumos`); onApplied?.(r.data); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="templates-dialog">
        <DialogHeader><DialogTitle className="font-display">Templates prontos</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Escolha um modelo. Criamos os insumos e a ficha técnica para você adaptar aos seus preços.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {(data || []).map((t) => (
            <button key={t.id} data-testid={`template-${t.id}`} onClick={() => apply(t)} disabled={!!busy}
              className="text-left rounded-xl border border-border p-4 hover:border-primary/60 hover:bg-primary/5 transition-colors disabled:opacity-60">
              <div className="flex items-center justify-between"><p className="font-display font-semibold">{t.name}</p><Badge variant="secondary">{t.category}</Badge></div>
              <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
              <p className="text-xs mt-2 num">{t.items_count} ingredientes · sugerido {brl(t.sale_price)}</p>
              {busy === t.id && <p className="text-xs text-primary mt-1">Aplicando...</p>}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MarginBadge = ({ r }) => {
  if (r.margin_pct == null) return <Badge variant="outline">sem preço</Badge>;
  return <span className={`num text-xs font-semibold px-2 py-0.5 rounded-full border ${r.margin_pct < 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-500" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"}`} data-testid={`recipe-margin-badge-${r.id}`}>margem {pct(r.margin_pct)}</span>;
};

export default function Recipes() {
  const { data: recipes, loading, reload } = useApi("/recipes");
  const { data: ingredients, reload: reloadIng } = useApi("/ingredients");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [tpl, setTpl] = useState(false);
  const [q, setQ] = useState("");
  const filtered = (recipes || []).filter((r) => `${r.name} ${r.category || ""} ${r.items.map((i) => i.ingredient_name).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); reloadIng(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader testId="recipes-page" title="Fichas Técnicas" subtitle="Cada prato com seus ingredientes, quantidades e o custo exato por porção."
        help="Ficha técnica é a 'receita com preço': lista tudo que entra no prato e quanto custa. Sem ela, você não sabe se está tendo lucro em cada venda." helpTitle="O que é ficha técnica?"
        actions={<>
          <Button variant="outline" onClick={() => setTpl(true)} data-testid="recipes-templates-btn"><LayoutTemplate className="w-4 h-4 mr-2" />Usar template</Button>
          <Button onClick={() => setDlg({ open: true, item: null })} data-testid="recipes-new-btn"><Plus className="w-4 h-4 mr-2" />Nova ficha</Button>
        </>} />

      {(recipes?.length > 0 || q) && (
        <div className="relative mb-5 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="recipes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar produto, categoria ou ingrediente..." className="pl-9" />
        </div>
      )}

      {!loading && !recipes?.length ? (
        <EmptyState testId="recipes-empty" icon={BookOpen} title="Nenhuma ficha técnica ainda" text="Comece por um template pronto (hambúrguer, pizza, açaí...) ou monte a sua do zero."
          action={<div className="flex gap-2 justify-center"><Button variant="outline" onClick={() => setTpl(true)} data-testid="recipes-empty-template">Ver templates</Button><Button onClick={() => setDlg({ open: true, item: null })} data-testid="recipes-empty-new">Criar do zero</Button></div>} />
      ) : !filtered.length ? (
        <EmptyState testId="recipes-search-empty" icon={Search} title="Nenhum produto encontrado" text={`Nada corresponde a "${q}". Tente outro nome, categoria ou ingrediente.`} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r, i) => (
            <div key={r.id} data-testid={`recipe-card-${r.id}`} className={`rise rise-${(i % 4) + 1} card-hover rounded-2xl border border-border bg-card p-5 flex flex-col gap-3`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <ProductImage recipe={r} size="md" testId={`recipe-image-${r.id}`} />
                  <div className="min-w-0"><h3 className="font-display font-semibold text-lg truncate">{r.name}</h3><p className="text-xs text-muted-foreground">{r.category || "Sem categoria"} · {r.items.length} ingredientes · rende {r.portions}</p></div>
                </div>
                <MarginBadge r={r} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center rounded-xl bg-secondary/50 p-3">
                <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Custo/porção</p><p className="num font-bold text-primary text-sm sm:text-base truncate" data-testid={`recipe-cpp-${r.id}`}>{brl(r.cost_per_portion)}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Preço {r.channel_name ? <span className="normal-case tracking-normal">({r.channel_name})</span> : ""}</p><p className="num font-bold text-sm sm:text-base truncate">{r.sale_price ? brl(r.sale_price) : "—"}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">CMV</p><p className="num font-bold text-sm sm:text-base truncate">{pct(r.cmv_pct)}</p></div>
              </div>
              {(r.prices_by_channel || []).some((v) => v.own && !v.is_default) && (
                <div className="flex flex-wrap gap-1.5" data-testid={`recipe-channel-prices-${r.id}`}>
                  {r.prices_by_channel.filter((v) => v.own && !v.is_default).map((v) => (
                    <span key={v.channel_id} title={`Margem líquida ${pct(v.contribution_margin_pct)} · taxas ${pct(v.fee_pct, 2)}`} className={`num text-[11px] px-2 py-0.5 rounded-full border ${v.contribution_margin != null && v.contribution_margin < 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-500" : "bg-secondary border-border"}`} data-testid={`recipe-channel-chip-${r.id}-${v.channel_id}`}>{v.channel_name} {brl(v.price)}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-auto">
                <span />
                <div className="flex">
                  <Button variant="ghost" size="icon" data-testid={`recipe-edit-${r.id}`} onClick={() => setDlg({ open: true, item: r })} aria-label="Editar"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`recipe-duplicate-${r.id}`} onClick={() => act(() => api.post(`/recipes/${r.id}/duplicate`), "Ficha duplicada")} aria-label="Duplicar"><Copy className="w-4 h-4" /></Button>
                  <ConfirmDelete what={`a ficha "${r.name}"`} onConfirm={() => act(() => api.delete(`/recipes/${r.id}`), "Ficha excluída")} testId={`recipe-delete-${r.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <RecipeDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} recipe={dlg.item} ingredients={ingredients || []} onSaved={reload} />
      <TemplatesDialog open={tpl} onOpenChange={setTpl} onApplied={() => { reload(); reloadIng(); }} />
    </div>
  );
}
